import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { WORK_ITEMS } from './workData';
import SpiraBot from './SpiraBot';
import './WorkEditorial.css';

function CoverVideo({ src, stopAt }) {
  const videoRef = useRef(null);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || stopAt == null || video.currentTime < stopAt) return;
    video.currentTime = 0;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  return (
    <video
      ref={videoRef}
      src={src}
      loop={stopAt == null}
      muted
      playsInline
      autoPlay
      preload="auto"
      onTimeUpdate={stopAt != null ? handleTimeUpdate : undefined}
    />
  );
}

function pillFor(item) {
  if (item.pill) return item.pill;
  if (item.comingSoon) return 'Coming soon';
  const label = `${item.eyebrow || ''} ${item.concept || ''}`.toLowerCase();
  if (label.includes('intern')) return 'Internship';
  if (label.includes('brand')) return 'Brand';
  if (label.includes('concept')) return 'Concept';
  return null;
}

function ProjectCard({ item }) {
  const slug = item.slug || item.name.toLowerCase().replace(/\s+/g, '-');
  const destination = item.comingSoonPath || (item.fullContent ? `/case-study/${slug}` : item.href);
  const isExternal = Boolean(destination && destination.startsWith('http'));
  const isStatic = item.comingSoon && !destination;
  const pill = pillFor(item);

  const LinkTag = isStatic ? 'div' : isExternal ? 'a' : Link;
  const linkProps = isStatic
    ? {}
    : isExternal
      ? { href: destination, target: '_blank', rel: 'noopener noreferrer' }
      : { to: destination };

  return (
    <LinkTag
      id={`project-${slug}`}
      className={`project-card${isStatic ? ' project-card--static' : ''}`}
      aria-label={`${item.name} — ${item.year}`}
      {...linkProps}
    >
      <div
        className={`project-thumb${item.thumb ? ' project-thumb--shaped' : ''}`}
        style={item.thumb ? {
          aspectRatio: item.thumb.ratio,
          background: item.thumb.bg,
          '--thumb-fit': item.thumb.fit,
          '--thumb-pos': item.thumb.position || 'center',
        } : undefined}
      >
        {/* Letterboxed covers fill their spare space with the video's own
            top edge, stretched and blurred, so it is the exact colour the
            browser draws — a hand-picked hex never quite matches video. */}
        {item.thumb?.fit === 'contain' && item.img?.endsWith('.mp4') && (
          <video
            className="project-thumb-fill"
            src={process.env.PUBLIC_URL + item.img}
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
          />
        )}
        {item.coverType === 'spira-bot' ? (
          <SpiraBot src={process.env.PUBLIC_URL + item.img} />
        ) : item.img && item.img.endsWith('.mp4') ? (
          <CoverVideo
            src={process.env.PUBLIC_URL + item.img}
            stopAt={item.name === 'Omnigence.ai' ? 2.6 : undefined}
          />
        ) : item.img ? (
          <img src={item.img} alt="" loading="lazy" />
        ) : null}
        {pill && (
          <div className="project-pills">
            <span>{pill}</span>
          </div>
        )}
      </div>

      <div className="project-meta">
        <span className="project-title">
          <span className="project-name">{item.name}:</span>{' '}
          {item.comingSoon ? 'AI tools for creators' : item.cardCaption || item.desc}
        </span>
        <span className="project-year">{item.year}</span>
      </div>
    </LinkTag>
  );
}

export default function WorkEditorial() {
  const left = WORK_ITEMS.filter((_, i) => i % 2 === 0);
  const right = WORK_ITEMS.filter((_, i) => i % 2 === 1);

  return (
    <section className="work-page" id="work" aria-label="Selected work">
      <div className="work-cols">
        <div className="work-col">
          {left.map((item) => <ProjectCard key={item.name} item={item} />)}
        </div>
        <div className="work-col">
          {right.map((item) => <ProjectCard key={item.name} item={item} />)}
        </div>
      </div>
    </section>
  );
}
